import React, { useState, useEffect, useRef } from 'react';
import { buildPlayerColorMap } from '../../utils/playerColors';
import useViewport, { SHORT_H } from '../../hooks/useViewport';

const VotingModal = ({
  players,
  objective,
  scene,
  currentPlayerId,
  votingData,
  onVote,
  onClose,
  theme,
  imposterMode = false,
  isPlayerGlitch = false,
  noirTheme = false, // Whodunnit-family: in-fiction copy ("strongest alibi", "booked")
  culpritNoun = null, // scenario pack reskin ("the fridge bandit"); null = classic "the killer"
  forceDismiss
}) => {
  const [selectedVote, setSelectedVote] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hoveredPlayer, setHoveredPlayer] = useState(null);
  const [voteFlash, setVoteFlash] = useState(false);
  const [revealCards, setRevealCards] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [flashingPlayers, setFlashingPlayers] = useState(new Set());
  const [isTallying, setIsTallying] = useState(false);
  const [tallyMessage, setTallyMessage] = useState('');
  const prevVoteCountsRef = useRef({});

  // Whodunnit finale — THE ACCUSATION: arrested players are accusable AND vote, chips can
  // be staked on the accusation, and the killer (fact_lineup present) guesses the briefing.
  const isAccusation = !!votingData?.is_accusation;
  const wagerMax = votingData?.wager_max || 0;
  const myPoints = votingData?.my_points || 0;
  const factLineup = votingData?.fact_lineup || null; // only ever sent to the killer
  const [wager, setWager] = useState(0);
  const [factGuess, setFactGuess] = useState(null);

  // Fun rotating messages for the tallying screen
  const tallyMessages = imposterMode && isPlayerGlitch ? [
    'Oh no, they\'re voting... 😰',
    'Act natural, act natural... 🫣',
    'Please don\'t pick me... 🙏',
    'Sweating profusely... 💦',
    'This is fine. Everything is fine... 🔥',
    'Maybe they bought it?... 🤞',
    'Your poker face better be good... 😬',
    'Running away is not an option... 🏃',
    'Deep breaths... deep breaths... 😮‍💨',
    'The votes are coming in... 👀',
  ] : imposterMode ? [
    'Analyzing suspects... 🔍',
    'Someone\'s sweating... 😰',
    'Scanning for glitches... 🤖',
    'The truth is about to surface... 🌊',
    'Cross-referencing the words... 📝',
    'One of you doesn\'t belong... 👀',
    'Decrypting the votes... 🔐',
    'The Golden Glitch is nervous... 😬',
    'Running detection protocols... ⚡',
    'This is gonna be good... 🍿',
  ] : [
    'Counting on our fingers... 🤚',
    'Double-checking the math... 🧮',
    'The suspense is killing us too... 😬',
    'Somebody\'s about to be real mad... 😏',
    'Tallying with dramatic flair... 💃',
    'Who snitched? Stay tuned... 🕵️',
    'Plot twist loading... 🔄',
    'The tea is being brewed... 🍵',
    'Consulting the oracle... 🔮',
    'This is gonna be spicy... 🌶️',
    'No take-backs now... 🫣',
    'Reading the room... 👀',
    'And the crowd goes wild... 🎉',
    'Crunching the drama numbers... 📊',
  ];

  useEffect(() => {
    if (!isTallying) return;
    const pick = () => tallyMessages[Math.floor(Math.random() * tallyMessages.length)];
    setTallyMessage(pick());
    const interval = setInterval(() => setTallyMessage(pick()), 2500);
    return () => clearInterval(interval);
  }, [isTallying]);

  // Countdown anchors to the SERVER deadline (voting_start_time + duration). The modal
  // can open well after voting started (reading banner, WS/refetch latency), and a fresh
  // full-duration timer here lies — votes past the server deadline 400 as expired.
  // Local mount-anchored deadline is the fallback, and also caps the server one so a
  // skewed client clock can never show MORE time than the full duration.
  const votingDuration = votingData?.duration || votingData?.voting_duration || 30;
  const mountTimeRef = useRef(Date.now());
  const deadlineRef = useRef(null);
  if (deadlineRef.current === null) {
    const startMs = votingData?.voting_start_time
      ? new Date(votingData.voting_start_time).getTime() : NaN;
    const localDeadline = mountTimeRef.current + votingDuration * 1000;
    deadlineRef.current = Number.isFinite(startMs)
      ? Math.min(startMs + votingDuration * 1000, localDeadline)
      : localDeadline;
  }
  const [timeRemaining, setTimeRemaining] = useState(() =>
    Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));

  // Staggered reveal animation
  useEffect(() => {
    const t1 = setTimeout(() => setShowHeader(true), 200);
    const t2 = setTimeout(() => setRevealCards(true), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        // Show tallying state — parent will dismiss us when vote results are ready
        setIsTallying(true);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [votingDuration]);

  // Check if current player has already voted
  useEffect(() => {
    if (votingData?.votes_cast?.[currentPlayerId]) {
      setHasVoted(true);
      setSelectedVote(votingData.votes_cast[currentPlayerId].voted_for);
    }
  }, [votingData, currentPlayerId]);

  // Parent signals us to close (vote results are ready to show)
  useEffect(() => {
    if (forceDismiss && isTallying) {
      handleClose();
    }
  }, [forceDismiss, isTallying]);

  // Auto-close tallying screen after a brief delay.
  // In imposter mode, forceDismiss is never set, so we need this fallback.
  // The parent (GameStepPage) will handle showing results/animations after we close.
  useEffect(() => {
    if (!isTallying) return;
    const timer = setTimeout(() => {
      handleClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [isTallying]);

  // Auto-close when all players have voted — show tallying transition first.
  // (Accusation finale: everyone votes, arrested included.)
  useEffect(() => {
    if (!votingData?.votes_cast || isTallying) return;
    const eligibleCount = players.filter(p => isAccusation || !p.data?.is_eliminated).length;
    const voteCount = Object.keys(votingData.votes_cast).length;
    if (voteCount >= eligibleCount && eligibleCount > 0) {
      setTimeout(() => {
        setIsTallying(true);
        // Parent will dismiss us when vote results are ready
      }, 1000);
    }
  }, [votingData?.votes_cast, players, isTallying]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose?.(), 500);
  };

  // Accusation finale: every seated suspect is a target — verdicts were sealed, so the
  // killer may well be sitting in holding. Normal rounds target the non-eliminated only.
  const eligiblePlayers = players.filter(p =>
    (isAccusation || !p.data?.is_eliminated) && p.id !== currentPlayerId
  );

  // Compact mode: many players OR a small viewport (round 3, 08-02 — the old
  // player-count-only trigger left a 4-player vote at full size on a phone,
  // overflowing both ends). Round 5 (08-03): the stopgap listener is gone —
  // width/height come from the shared useViewport store; the 480 cutoff is
  // this modal's own and stays local.
  const { width: vpW, height: vpH } = useViewport();
  const smallViewport = vpW < 480 || vpH < SHORT_H;
  const isCompact = smallViewport || eligiblePlayers.length >= 5;

  const handleVote = async (playerId) => {
    if (playerId === selectedVote) return; // Already selected this player
    const player = players.find(p => p.id === playerId);
    const previousVote = selectedVote;
    setSelectedVote(playerId);
    setHasVoted(true);
    setVoteFlash(true);
    setTimeout(() => setVoteFlash(false), 600);

    try {
      await onVote({
        voted_for: playerId,
        target_name: player.name,
        ...(isAccusation ? {
          wager,
          ...(factGuess ? { fact_guess: factGuess } : {})
        } : {})
      });
    } catch (error) {
      console.error('Failed to submit vote:', error);
      // Revert to previous vote on error
      setSelectedVote(previousVote);
      if (!previousVote) setHasVoted(false);
    }
  };

  // Calculate vote counts
  const voteCounts = {};
  let maxVotes = 0;
  if (votingData?.votes_cast) {
    Object.values(votingData.votes_cast).forEach(vote => {
      voteCounts[vote.voted_for] = (voteCounts[vote.voted_for] || 0) + 1;
      maxVotes = Math.max(maxVotes, voteCounts[vote.voted_for]);
    });
  }

  const totalVotesCast = Object.keys(votingData?.votes_cast || {}).length;

  // Detect vote count changes and flash the affected player card
  useEffect(() => {
    const newFlashing = new Set();
    for (const [playerId, count] of Object.entries(voteCounts)) {
      if ((prevVoteCountsRef.current[playerId] || 0) < count) {
        newFlashing.add(playerId);
      }
    }
    prevVoteCountsRef.current = { ...voteCounts };
    if (newFlashing.size > 0) {
      setFlashingPlayers(newFlashing);
      const t = setTimeout(() => setFlashingPlayers(new Set()), 600);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalVotesCast]);
  const totalPlayers = players.filter(p => !p.data?.is_eliminated).length;
  const timerFraction = timeRemaining / votingDuration;
  const isUrgent = timeRemaining <= 10;
  const isCritical = timeRemaining <= 5;

  const accent = theme.accent || '#6366f1';

  // One stable color per seat, keyed by player id and built from the FULL roster —
  // matches the roster/chat/dice-toast colors exactly (utils/playerColors), so "vote
  // for the purple one" survives the trip from the chat log to this ballot.
  const seatColors = buildPlayerColorMap(players);
  const getPlayerColor = (player) => seatColors[String(player?.game_player_id || player?.id || '')] || '#6366f1';

  // Timer color
  const getTimerColor = () => {
    if (isCritical) return '#ff3b3b';
    if (isUrgent) return '#ff8c00';
    return accent;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        opacity: isClosing ? 0 : 1,
        transition: 'opacity 0.5s ease',
        backdropFilter: 'blur(20px)',
        // Was overflow:hidden on BOTH this and the card, with no whole-card
        // scroller — so on a short screen (375x812 in accusation mode: header +
        // objective + wager stepper + 45dvh list + footer) the content simply
        // got clipped at BOTH ends: the stepper off the top, the footer off the
        // bottom, with no way to reach either. Let the backdrop scroll and pad
        // it so a too-tall card is reachable instead of sheared.
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '12px 0',
      }}
      onClick={(e) => {
        // Don't allow dismissing by clicking outside — votes must be cast or timer must expire
      }}
    >
      {/* Dramatic spotlight / radial glow behind the modal */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '800px',
        height: '800px',
        background: `radial-gradient(circle, ${accent}12 0%, ${accent}06 30%, transparent 70%)`,
        pointerEvents: 'none',
        animation: 'votingSpotlightPulse 3s ease-in-out infinite'
      }} />

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: `${accent}40`,
          animation: `votingParticle ${3 + i * 0.5}s ease-in-out infinite`,
          animationDelay: `${i * 0.3}s`,
          left: `${15 + i * 14}%`,
          top: `${20 + (i % 3) * 25}%`
        }} />
      ))}

      {/* Vote flash overlay */}
      {voteFlash && (
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: `${accent}`,
          opacity: 0,
          animation: 'votingFlashBang 0.6s ease-out',
          pointerEvents: 'none',
          zIndex: 10001
        }} />
      )}

      {/* Main modal container */}
      <div
        style={{
          position: 'relative',
          backgroundColor: theme.bgMedium || '#0d0d1a',
          borderRadius: '24px',
          maxWidth: '520px',
          width: '94%',
          // Cap the card and let its body scroll rather than overflowing the
          // viewport (see the backdrop note). flexShrink:0 stops the flex
          // backdrop from squashing it once it scrolls.
          maxHeight: 'calc(100dvh - 24px)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          margin: 'auto',
          boxShadow: `
            0 0 0 1px ${accent}25,
            0 0 60px ${accent}15,
            0 40px 100px rgba(0,0,0,0.8)
          `,
          transform: isClosing
            ? 'scale(0.9) translateY(30px)'
            : 'scale(1) translateY(0)',
          transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease',
          animation: 'votingModalEntry 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
          overflow: 'hidden'
        }}
      >
        {/* X close button */}
        {!isTallying && (
          <button
            onClick={() => setIsTallying(true)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              // 40px close target (round 3, 08-02 — was 32px, under the 44
              // floor; 40 is the composer-cluster compromise the audit accepts)
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.1)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.1em',
              fontWeight: 300,
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.4)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            }}
          >
            ✕
          </button>
        )}

        {/* Top glow bar */}
        <div style={{
          height: '3px',
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.8
        }} />

        {/* Timer progress bar */}
        <div style={{
          height: '4px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${timerFraction * 100}%`,
            background: isCritical
              ? 'linear-gradient(90deg, #ff3b3b, #ff6b3b)'
              : isUrgent
                ? 'linear-gradient(90deg, #ff8c00, #ffaa00)'
                : `linear-gradient(90deg, ${accent}, ${accent}cc)`,
            transition: 'width 0.1s linear',
            boxShadow: isCritical ? '0 0 20px #ff3b3b80' : 'none'
          }} />
        </div>

        {/* The card's scrolling body — the only reason the card can be capped
            without shearing content off (padding trimmed on narrow screens,
            where 32px a side was eating a tenth of the width). */}
        <div style={{
          padding: 'clamp(18px, 5vw, 32px) clamp(16px, 5vw, 32px) clamp(18px, 4vw, 28px)',
          overflowY: 'auto',
          minHeight: 0,
          flex: '1 1 auto',
        }}>

          {isTallying ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 24px',
              animation: 'votingSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            }}>
              <div style={{
                fontSize: '3.5em',
                marginBottom: '20px',
                animation: 'votingTallySpin 1.5s ease-in-out infinite',
              }}>
                {'\u{1F5F3}\uFE0F'}
              </div>
              <h2 style={{
                color: '#fff',
                fontSize: '1.4em',
                fontWeight: 900,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                marginBottom: '12px',
                textShadow: `0 0 30px ${accent}40`,
                whiteSpace: 'nowrap',
              }}>
                {imposterMode ? (isPlayerGlitch ? 'Please Not Me...' : 'Identifying The Golden Glitch...') : isAccusation ? 'The Jury Deliberates...' : noirTheme ? 'The Detective Deliberates...' : 'Votes Are Being Counted!'}
              </h2>
              <div style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.9em',
                fontWeight: 600,
                minHeight: '1.4em',
                transition: 'opacity 0.3s ease',
              }}>
                {tallyMessage}
              </div>
              <div style={{
                width: '40px',
                height: '3px',
                background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                marginTop: '24px',
                animation: 'votingTallyBar 1.2s ease-in-out infinite',
              }} />
            </div>
          ) : (
          <>
          {/* ── HEADER ── */}
          <div style={{
            textAlign: 'center',
            marginBottom: '28px',
            opacity: showHeader ? 1 : 0,
            transform: showHeader ? 'translateY(0)' : 'translateY(-20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            {/* Round badge */}
            <div style={{
              display: 'inline-block',
              padding: '5px 16px',
              borderRadius: '20px',
              backgroundColor: `${accent}15`,
              border: `1px solid ${accent}30`,
              fontSize: '0.7em',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '3px',
              color: accent,
              marginBottom: '14px'
            }}>
              {imposterMode ? (isPlayerGlitch ? 'SURVIVAL ROUND' : 'GLITCH HUNT') : isAccusation ? 'THE ACCUSATION' : `Round ${votingData?.round_number}`}
            </div>

            {/* Big title */}
            <h2 style={{
              color: imposterMode ? (isPlayerGlitch ? '#ffaa00' : '#ff4444') : '#fff',
              margin: '0 0 20px',
              fontSize: '2em',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              textShadow: imposterMode
                ? (isPlayerGlitch ? '0 0 40px rgba(255,170,0,0.4)' : '0 0 40px rgba(255,0,0,0.4)')
                : `0 0 40px ${accent}40`
            }}>
              {imposterMode
                ? (isPlayerGlitch ? 'DON\'T GET CAUGHT' : 'FIND THE GOLDEN GLITCH')
                : isAccusation
                  ? (factLineup ? 'FRAME A SCAPEGOAT' : 'WHO DID IT?')
                  : noirTheme
                    ? 'STRONGEST ALIBI?'
                    : 'CAST YOUR VOTE'}
            </h2>
            <div style={{
              fontSize: '1.1em',
              color: imposterMode ? (isPlayerGlitch ? 'rgba(255,200,100,0.8)' : 'rgba(255,100,100,0.8)') : accent,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}>
              {imposterMode
                ? (isPlayerGlitch ? "Time to hope you don't get squashed! 🐛" : "Who doesn't belong? 🔍")
                : isAccusation
                  ? (factLineup
                      ? "You did it. Point them somewhere else... 🔪"
                      : `Name ${culpritNoun || 'the killer'}. Charge wrong and they walk! ⚖️`)
                  : noirTheme
                    ? 'Whose story held up? The weakest gets booked 🚔'
                    : 'Who clocked it? 💅'}
            </div>
          </div>

          {/* ── OBJECTIVE + PROMPT ── */}
          <div style={{
            marginBottom: '24px',
            textAlign: 'center',
            opacity: showHeader ? 1 : 0,
            transform: showHeader ? 'translateY(0)' : 'translateY(10px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s'
          }}>
            {objective && objective !== scene && (
              <div style={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: '0.78em',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: scene ? '6px' : 0,
              }}>
                {objective}
              </div>
            )}
            {scene && (
              <div style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: '0.92em',
                lineHeight: 1.5,
                fontStyle: 'italic',
              }}>
                "{scene}"
              </div>
            )}
          </div>

          {/* ── ACCUSATION EXTRAS: chip wager + (killer only) case-file guess ── */}
          {isAccusation && !hasVoted && (
            <div style={{ marginBottom: '18px' }}>
              {/* Wager stepper — set the stake BEFORE tapping a suspect (tap submits) */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.08)',
                backgroundColor: 'rgba(255,255,255,0.03)',
                marginBottom: factLineup ? '10px' : 0
              }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.85em' }}>
                    💰 Stake chips on your accusation
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72em', marginTop: '2px' }}>
                    {wagerMax > 0
                      ? `You have ${myPoints} · stake up to ${wagerMax} · right +${wager || '?'}, wrong −${wager || '?'}`
                      : 'No chips to stake yet — accuse on the house'}
                  </div>
                </div>
                {wagerMax > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={() => setWager(w => Math.max(0, w - 1))}
                      disabled={wager <= 0}
                      style={{
                        // 42px steppers (round 3, 08-02 — 30px was the worst
                        // sub-44 target in the accusation flow)
                        width: '42px', height: '42px', borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.15)',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        color: '#fff', fontWeight: 800, cursor: wager > 0 ? 'pointer' : 'default',
                        opacity: wager > 0 ? 1 : 0.35
                      }}
                    >−</button>
                    <div style={{
                      minWidth: '28px', textAlign: 'center',
                      color: wager > 0 ? '#ffd166' : 'rgba(255,255,255,0.6)',
                      fontWeight: 900, fontSize: '1.15em'
                    }}>{wager}</div>
                    <button
                      onClick={() => setWager(w => Math.min(wagerMax, w + 1))}
                      disabled={wager >= wagerMax}
                      style={{
                        width: '42px', height: '42px', borderRadius: '50%',
                        border: '1px solid rgba(255,209,102,0.4)',
                        backgroundColor: 'rgba(255,209,102,0.12)',
                        color: '#ffd166', fontWeight: 800, cursor: wager < wagerMax ? 'pointer' : 'default',
                        opacity: wager < wagerMax ? 1 : 0.35
                      }}
                    >+</button>
                  </div>
                )}
              </div>

              {/* Killer's case-file lineup — guess the briefing you never saw (+2) */}
              {factLineup && factLineup.length > 0 && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,140,102,0.25)',
                  backgroundColor: 'rgba(255,140,102,0.06)'
                }}>
                  <div style={{ color: '#ff8c66', fontWeight: 800, fontSize: '0.8em', marginBottom: '8px' }}>
                    🗂️ What did the briefing say? You never saw it — guess right for +2
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {factLineup.map((fact, i) => (
                      <button
                        key={i}
                        onClick={() => setFactGuess(factGuess === fact ? null : fact)}
                        style={{
                          textAlign: 'left',
                          padding: '7px 10px',
                          borderRadius: '8px',
                          border: factGuess === fact
                            ? '1px solid #ff8c66'
                            : '1px solid rgba(255,255,255,0.08)',
                          backgroundColor: factGuess === fact
                            ? 'rgba(255,140,102,0.15)'
                            : 'rgba(255,255,255,0.03)',
                          color: factGuess === fact ? '#fff' : 'rgba(255,255,255,0.7)',
                          fontSize: '0.78em',
                          lineHeight: 1.35,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {factGuess === fact ? '◉ ' : '○ '}{fact}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── PLAYER VOTE CARDS ── */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isCompact ? '6px' : '10px',
            marginBottom: '24px',
            // dvh: vh over-measures on phones while the browser chrome is
            // visible, pushing the confirm button off-screen (mobile audit
            // wave 2, 07-17)
            maxHeight: '45dvh',
            overflowY: 'auto',
          }}>
            {eligiblePlayers.map((player, index) => {
              const voteCount = voteCounts[player.id] || 0;
              const isSelected = selectedVote === player.id;
              const isHovered = hoveredPlayer === player.id;
              const barWidth = maxVotes > 0 ? (voteCount / maxVotes) * 100 : 0;
              const isLeading = voteCount > 0 && voteCount === maxVotes;
              const isFlashing = flashingPlayers.has(player.id);

              return (
                <button
                  key={player.id}
                  onClick={() => handleVote(player.id)}
                  onMouseEnter={() => setHoveredPlayer(player.id)}
                  onMouseLeave={() => setHoveredPlayer(null)}
                  disabled={isTallying}
                  style={{
                    padding: isCompact ? '10px 14px' : '16px 18px',
                    borderRadius: isCompact ? '10px' : '14px',
                    border: isSelected
                      ? `2px solid ${accent}`
                      : `1px solid ${isHovered && !isTallying ? accent + '50' : 'rgba(255,255,255,0.08)'}`,
                    backgroundColor: isFlashing
                      ? `${accent}25`
                      : isSelected
                        ? `${accent}18`
                        : isHovered && !isTallying
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(255,255,255,0.03)',
                    cursor: isTallying ? 'default' : 'pointer',
                    opacity: revealCards ? (hasVoted && !isSelected ? 0.6 : 1) : 0,
                    transform: revealCards
                      ? (isHovered && !isTallying ? 'translateX(6px) scale(1.01)' : (isSelected ? 'scale(1.02)' : 'none'))
                      : 'translateX(-30px)',
                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    transitionDelay: revealCards ? `${index * 0.08}s` : '0s',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'left',
                    boxShadow: isFlashing
                      ? `0 0 20px ${accent}50, inset 0 0 20px ${accent}15`
                      : isSelected
                        ? `0 0 30px ${accent}20, inset 0 0 30px ${accent}08`
                        : 'none'
                  }}
                >
                  {/* Vote count bar */}
                  {voteCount > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${barWidth}%`,
                      background: isSelected
                        ? `linear-gradient(90deg, ${accent}40, ${accent}18)`
                        : `linear-gradient(90deg, ${accent}30, ${accent}0a)`,
                      transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                      zIndex: 0
                    }} />
                  )}

                  {/* Leading indicator line */}
                  {isLeading && voteCount > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '3px',
                      height: '100%',
                      backgroundColor: accent,
                      boxShadow: `0 0 10px ${accent}60`
                    }} />
                  )}

                  {/* Content */}
                  <div style={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isCompact ? '10px' : '14px'
                    }}>
                      {/* Avatar */}
                      <div style={{ position: 'relative' }}>
                        {player.profile_image_url ? (
                          (() => {
                            const pColor = getPlayerColor(player);
                            return (
                              <img
                                src={player.profile_image_url}
                                alt={player.name}
                                style={{
                                  width: isCompact ? '34px' : '44px',
                                  height: isCompact ? '34px' : '44px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: isSelected
                                    ? `2px solid ${pColor}`
                                    : `2px solid ${pColor}40`,
                                  boxShadow: isSelected ? `0 0 16px ${pColor}40` : 'none',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            );
                          })()
                        ) : (
                          (() => {
                            const pColor = getPlayerColor(player);
                            return (
                              <div style={{
                                width: isCompact ? '34px' : '44px',
                                height: isCompact ? '34px' : '44px',
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${pColor}30, ${pColor}15)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isCompact ? '0.9em' : '1.1em',
                                fontWeight: 800,
                                color: pColor,
                                border: isSelected
                                  ? `2px solid ${pColor}`
                                  : '2px solid rgba(255,255,255,0.1)',
                                boxShadow: isSelected ? `0 0 16px ${pColor}40` : 'none',
                                transition: 'all 0.3s ease'
                              }}>
                                {player.name?.charAt(0)?.toUpperCase()}
                              </div>
                            );
                          })()
                        )}
                        {/* Selected glow ring */}
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            inset: '-4px',
                            borderRadius: '50%',
                            border: `2px solid ${accent}40`,
                            animation: 'votingRingPulse 1.5s ease-in-out infinite'
                          }} />
                        )}
                      </div>

                      <div>
                        <div style={{
                          fontWeight: 700,
                          fontSize: isCompact ? '0.9em' : '1.05em',
                          color: '#fff',
                          letterSpacing: '-0.01em',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {player.name}
                          {isAccusation && player.data?.is_eliminated && (
                            <span style={{
                              fontSize: '0.68em',
                              fontWeight: 800,
                              color: '#ff8c66',
                              backgroundColor: 'rgba(255,140,102,0.12)',
                              border: '1px solid rgba(255,140,102,0.3)',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              letterSpacing: '0.5px',
                              whiteSpace: 'nowrap'
                            }}>
                              🚔 IN HOLDING
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {/* Vote count badge */}
                      {voteCount > 0 && (
                        <div style={{
                          fontSize: '0.8em',
                          fontWeight: 800,
                          color: isLeading ? '#fff' : accent,
                          backgroundColor: isLeading ? accent : `${accent}18`,
                          padding: '4px 12px',
                          borderRadius: '20px',
                          letterSpacing: '0.02em',
                          animation: 'votingBadgePop 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                          boxShadow: isLeading ? `0 0 16px ${accent}40` : 'none'
                        }}>
                          {voteCount} vote{voteCount !== 1 ? 's' : ''}{totalVotesCast > 0 ? ` (${Math.round((voteCount / totalVotesCast) * 100)}%)` : ''}
                        </div>
                      )}

                      {/* Selected check */}
                      {isSelected && (
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${accent}, ${accent}dd)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          animation: 'votingCheckBounce 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                          boxShadow: `0 0 20px ${accent}50`
                        }}>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M3 7L6 10L11 4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}

                      {/* Hover arrow indicator */}
                      {isHovered && !isTallying && !isSelected && (
                        <div style={{
                          color: accent,
                          fontSize: '1.2em',
                          animation: 'votingArrowBounce 0.6s ease-in-out infinite'
                        }}>
                          ›
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── FOOTER ── */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '8px',
            borderTop: '1px solid rgba(255,255,255,0.06)'
          }}>
            {hasVoted ? (
              <button
                onClick={() => setIsTallying(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 18px',
                  borderRadius: '20px',
                  border: `1px solid ${accent}40`,
                  backgroundColor: `${accent}15`,
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.8em',
                  fontWeight: 700,
                  transition: 'all 0.2s ease',
                  animation: 'votingSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  marginLeft: 'auto',
                  order: 2,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${accent}30`;
                  e.currentTarget.style.borderColor = `${accent}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${accent}15`;
                  e.currentTarget.style.borderColor = `${accent}40`;
                }}
              >
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                    <path d="M3 7L6 10L11 4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                Done
              </button>
            ) : (
              <div style={{
                color: 'rgba(255,255,255,0.25)',
                fontSize: '0.8em',
                fontWeight: 500,
              }}>
                {imposterMode ? (isPlayerGlitch ? 'Vote to throw them off your scent' : 'Tap who you think is The Golden Glitch') : 'Tap a player to vote'}
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                fontSize: '0.72em',
                color: 'rgba(255,255,255,0.3)',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 600
              }}>
                {totalVotesCast}/{totalPlayers}
              </span>

              {/* Timer */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 10px',
                borderRadius: '16px',
                backgroundColor: `${getTimerColor()}10`,
                border: `1px solid ${getTimerColor()}25`,
                animation: isCritical ? 'votingUrgentPulse 0.5s ease-in-out infinite' : 'none'
              }}>
                <svg width="11" height="11" viewBox="0 0 12 12">
                  <circle cx="6" cy="6" r="5" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />
                  <circle
                    cx="6" cy="6" r="5" fill="none"
                    stroke={getTimerColor()} strokeWidth="1.5"
                    strokeDasharray={`${timerFraction * 31.4} 31.4`}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dasharray 0.1s linear' }}
                  />
                </svg>
                <span style={{
                  fontSize: '0.8em',
                  fontWeight: 800,
                  color: getTimerColor(),
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {timeRemaining}s
                </span>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

// Animations
if (typeof document !== 'undefined') {
  const styleId = 'voting-modal-styles-v3';
  if (!document.head.querySelector(`style[data-id="${styleId}"]`)) {
    const styleSheet = document.createElement('style');
    styleSheet.setAttribute('data-id', styleId);
    styleSheet.textContent = `
      @keyframes votingModalEntry {
        0% {
          opacity: 0;
          transform: scale(0.85) translateY(40px);
        }
        50% {
          opacity: 1;
        }
        100% {
          transform: scale(1) translateY(0);
        }
      }

      @keyframes votingSpotlightPulse {
        0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
      }

      @keyframes votingParticle {
        0%, 100% { opacity: 0; transform: translateY(0) scale(0.5); }
        50% { opacity: 0.8; transform: translateY(-40px) scale(1); }
      }

      @keyframes votingFlashBang {
        0% { opacity: 0.3; }
        100% { opacity: 0; }
      }

      @keyframes votingCheckBounce {
        0% { transform: scale(0) rotate(-20deg); }
        60% { transform: scale(1.2) rotate(5deg); }
        100% { transform: scale(1) rotate(0deg); }
      }

      @keyframes votingBadgePop {
        0% { transform: scale(0.5); opacity: 0; }
        60% { transform: scale(1.15); }
        100% { transform: scale(1); opacity: 1; }
      }

      @keyframes votingRingPulse {
        0%, 100% { opacity: 0.4; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.1); }
      }

      @keyframes votingArrowBounce {
        0%, 100% { transform: translateX(0); }
        50% { transform: translateX(4px); }
      }

      @keyframes votingUrgentPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.05); }
      }

      @keyframes votingSlideUp {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes votingTallySpin {
        0% { transform: rotate(0deg) scale(1); }
        25% { transform: rotate(-10deg) scale(1.1); }
        50% { transform: rotate(10deg) scale(1); }
        75% { transform: rotate(-5deg) scale(1.05); }
        100% { transform: rotate(0deg) scale(1); }
      }

      @keyframes votingTallyBar {
        0%, 100% { width: 40px; opacity: 0.4; }
        50% { width: 120px; opacity: 1; }
      }
    `;
    document.head.appendChild(styleSheet);
  }

  // Clean up old v2 styles
  const oldStyle = document.head.querySelector('style[data-id="voting-modal-styles-v2"]');
  if (oldStyle) oldStyle.remove();
}

export default VotingModal;
